'use client';

import { c as _c } from "react/compiler-runtime";
import type { Blog, ForwardingsPerProject, FundraisingEventFieldsFragment, Maybe, Sponsor } from 'api-graphql-types';
import { LayoutStack } from 'design-system/server';
import { useMemo, useRef, useState } from 'react';
import BottomCallToAction from './BottomCallToAction';
import DonateAndShareSection from './DonateAndShareSection';
import StickyBar from './DonateAndShareSection/StickyBar';
import DonationList from './DonationList';
import ForwardingOverview from './ForwardingOverview';
import FundraisingEventDescription from './FundraisingEventDescription';
import styles from './FundraisingEventPageContent.module.css';
import FundraisingEventQuickLinks from './FundraisingEventQuickLinks';
import HeroSection from './HeroSection';
import News from './News';
import Progress from './Progress';
import ProgressAndDonations from './ProgressAndDonations';
import SupportedProjects from './SupportedProjects';
import type { FEPPContentDonation } from './types';
import useDonationFormUrl from './useDonationFormUrl';

//@ts-expect-error TODO: DEV-27674 remove this once we are sure we won't be coming back to this solution
function ProgressAndDonationsWithoutPolling(t0) {
  const $ = _c(14);
  const {
    receivedAmountInCents,
    receivedDonationsCount: receivedDonationsCount_,
    sponsor,
    target,
    fundraisingEventId,
    donationFormUrl,
    title,
    donations
  } = t0;
  const [receivedDonationsCount, setReceiveDonationsCount] = useState(receivedDonationsCount_);
  let t1;
  if ($[0] !== receivedAmountInCents || $[1] !== receivedDonationsCount || $[2] !== sponsor || $[3] !== target) {
    t1 = <Progress sponsor={sponsor} target={target} donationAmount={receivedAmountInCents} receivedDonationsCount={receivedDonationsCount} />;
    $[0] = receivedAmountInCents;
    $[1] = receivedDonationsCount;
    $[2] = sponsor;
    $[3] = target;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== donationFormUrl || $[6] !== donations || $[7] !== fundraisingEventId || $[8] !== receivedDonationsCount_ || $[9] !== title) {
    t2 = <DonationList fundraisingEventId={fundraisingEventId} donationFormUrl={donationFormUrl} title={title} donations={donations} receivedDonationsCount={receivedDonationsCount_} onDonationCountChange={setReceiveDonationsCount} />;
    $[5] = donationFormUrl;
    $[6] = donations;
    $[7] = fundraisingEventId;
    $[8] = receivedDonationsCount_;
    $[9] = title;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== t1 || $[12] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}
function ProgressAndDonationsVariants(t0) {
  const $ = _c(9);
  const {
    receivedAmountInCents,
    receivedDonationsCount,
    sponsor,
    targetAmountInCents,
    fundraisingEventId,
    donationFormUrl,
    donations,
    initialDonationLimit
  } = t0;
  let t1;
  if ($[0] !== donationFormUrl || $[1] !== donations || $[2] !== fundraisingEventId || $[3] !== initialDonationLimit || $[4] !== receivedAmountInCents || $[5] !== receivedDonationsCount || $[6] !== sponsor || $[7] !== targetAmountInCents) {
    t1 = <ProgressAndDonations sponsor={sponsor} target={targetAmountInCents} receivedAmountInCents={receivedAmountInCents} fundraisingEventId={fundraisingEventId} donationFormUrl={donationFormUrl} donations={donations} receivedDonationsCount={receivedDonationsCount} initialDonationLimit={initialDonationLimit} />;
    $[0] = donationFormUrl;
    $[1] = donations;
    $[2] = fundraisingEventId;
    $[3] = initialDonationLimit;
    $[4] = receivedAmountInCents;
    $[5] = receivedDonationsCount;
    $[6] = sponsor;
    $[7] = targetAmountInCents;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}
function FundraisingEventPageContent(t0) {
  const $ = _c(75);
  const {
    fundraisingEvent,
    title,
    domain,
    slug,
    donations,
    initialDonationLimit
  } = t0;
  const {
    id,
    backgroundColor,
    videoUrl,
    profilePictures,
    publicFace,
    name,
    sponsor,
    headerImageUrl,
    description,
    targetAmountInCents,
    receivedAmountInCents,
    receivedDonationsCount,
    projects,
    blogs,
    completeForwardingsPerProject,
    forwardedAmountInCents,
    aasmState
  } = fundraisingEvent;
  const isSponsored = !!sponsor;
  const stickySpyRef = useRef(null);
  const isClosed = aasmState === "closed";
  const isBlocked = aasmState === "blocked";
  let t1;
  if ($[0] !== fundraisingEvent.id) {
    t1 = {
      fundraisingEventId: fundraisingEvent.id
    };
    $[0] = fundraisingEvent.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const donationFormUrl = useDonationFormUrl(t1);
  const t2 = isSponsored ? sponsor.imageUrl420x230 : publicFace.imageUrl100x100;
  const t3 = isSponsored ? sponsor.name : publicFace.name;
  let t4;
  if ($[2] !== backgroundColor || $[3] !== headerImageUrl || $[4] !== isSponsored || $[5] !== name || $[6] !== profilePictures.fill730x380Url || $[7] !== publicFace.id || $[8] !== t2 || $[9] !== t3 || $[10] !== videoUrl) {
    t4 = <HeroSection backgroundColor={backgroundColor} heroVideo={videoUrl} heroImage={profilePictures.fill730x380Url} isSponsored={isSponsored} publicFaceImage={t2} publicFaceName={t3} publicFaceId={publicFace.id} fundraisingEventName={name} headerImageUrl={headerImageUrl} />;
    $[2] = backgroundColor;
    $[3] = headerImageUrl;
    $[4] = isSponsored;
    $[5] = name;
    $[6] = profilePictures.fill730x380Url;
    $[7] = publicFace.id;
    $[8] = t2;
    $[9] = t3;
    $[10] = videoUrl;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== donationFormUrl || $[13] !== id || $[14] !== isBlocked || $[15] !== isClosed || $[16] !== name || $[17] !== slug || $[18] !== title) {
    t5 = !isBlocked && <DonateAndShareSection donationFormUrl={donationFormUrl} fundraisingEventName={name} stickyIntersectionSpyRef={stickySpyRef} isClosed={isClosed} fundraisingEventId={id} slug={slug} title={title} />;
    $[12] = donationFormUrl;
    $[13] = id;
    $[14] = isBlocked;
    $[15] = isClosed;
    $[16] = name;
    $[17] = slug;
    $[18] = title;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== donationFormUrl || $[21] !== donations || $[22] !== id || $[23] !== initialDonationLimit || $[24] !== receivedAmountInCents || $[25] !== receivedDonationsCount || $[26] !== sponsor || $[27] !== targetAmountInCents || $[28] !== title) {
    t6 = <ProgressAndDonationsVariants sponsor={sponsor} targetAmountInCents={targetAmountInCents} receivedAmountInCents={receivedAmountInCents} fundraisingEventId={id} donationFormUrl={donationFormUrl} title={title} donations={donations} receivedDonationsCount={receivedDonationsCount} initialDonationLimit={initialDonationLimit} />;
    $[20] = donationFormUrl;
    $[21] = donations;
    $[22] = id;
    $[23] = initialDonationLimit;
    $[24] = receivedAmountInCents;
    $[25] = receivedDonationsCount;
    $[26] = sponsor;
    $[27] = targetAmountInCents;
    $[28] = title;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  let t7;
  if ($[30] !== description || $[31] !== fundraisingEvent.createdAt || $[32] !== publicFace) {
    t7 = <FundraisingEventDescription description={description} publicFace={publicFace} createdAt={fundraisingEvent.createdAt} />;
    $[30] = description;
    $[31] = fundraisingEvent.createdAt;
    $[32] = publicFace;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className={styles.verticalLine} />;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== projects || $[36] !== publicFace.name) {
    t9 = <SupportedProjects projects={projects} publicFaceName={publicFace.name} />;
    $[35] = projects;
    $[36] = publicFace.name;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  let t10;
  if ($[38] !== t7 || $[39] !== t9) {
    t10 = <section className={styles.descriptionAndProjectsContainer}>{t7}{t8}{t9}</section>;
    $[38] = t7;
    $[39] = t9;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== blogs || $[42] !== publicFace.imageUrl100x100 || $[43] !== publicFace.name) {
    t11 = !!blogs.length && <News news={blogs as Blog[]} publicFaceName={publicFace.name} publicFaceImage={publicFace.imageUrl100x100} />;
    $[41] = blogs;
    $[42] = publicFace.imageUrl100x100;
    $[43] = publicFace.name;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  const t12 = completeForwardingsPerProject as ForwardingsPerProject[];
  let t13;
  if ($[45] !== forwardedAmountInCents || $[46] !== publicFace.name || $[47] !== receivedAmountInCents || $[48] !== t12) {
    t13 = <ForwardingOverview completeForwardingsPerProject={t12} forwardedAmountInCents={forwardedAmountInCents} publicFaceName={publicFace.name} receivedAmountInCents={receivedAmountInCents} />;
    $[45] = forwardedAmountInCents;
    $[46] = publicFace.name;
    $[47] = receivedAmountInCents;
    $[48] = t12;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  let t14;
  if ($[50] !== donationFormUrl || $[51] !== id || $[52] !== isBlocked || $[53] !== isClosed) {
    t14 = !isBlocked && !isClosed && <BottomCallToAction donationFormUrl={donationFormUrl} fundraisingEventId={id} />;
    $[50] = donationFormUrl;
    $[51] = id;
    $[52] = isBlocked;
    $[53] = isClosed;
    $[54] = t14;
  } else {
    t14 = $[54];
  }
  let t15;
  if ($[55] !== donationFormUrl || $[56] !== id || $[57] !== name || $[58] !== slug || $[59] !== title) {
    t15 = <StickyBar spyRef={stickySpyRef} donationFormUrl={donationFormUrl} mobileBar={true} fundraisingEventId={id} slug={slug} title={title}>{name}</StickyBar>;
    $[55] = donationFormUrl;
    $[56] = id;
    $[57] = name;
    $[58] = slug;
    $[59] = title;
    $[60] = t15;
  } else {
    t15 = $[60];
  }
  let t16;
  if ($[61] !== domain || $[62] !== fundraisingEvent.id || $[63] !== publicFace.id) {
    t16 = <FundraisingEventQuickLinks fundraisingEventId={fundraisingEvent.id} publicFaceId={publicFace.id} domain={domain} />;
    $[61] = domain;
    $[62] = fundraisingEvent.id;
    $[63] = publicFace.id;
    $[64] = t16;
  } else {
    t16 = $[64];
  }
  let t17;
  if ($[65] !== t10 || $[66] !== t11 || $[67] !== t13 || $[68] !== t14 || $[69] !== t15 || $[70] !== t16 || $[71] !== t4 || $[72] !== t5 || $[73] !== t6) {
    t17 = <LayoutStack space="none" className={styles.feppContentContainer}>{t4}{t5}{t6}{t10}{t11}{t13}{t14}{t15}{t16}</LayoutStack>;
    $[65] = t10;
    $[66] = t11;
    $[67] = t13;
    $[68] = t14;
    $[69] = t15;
    $[70] = t16;
    $[71] = t4;
    $[72] = t5;
    $[73] = t6;
    $[74] = t17;
  } else {
    t17 = $[74];
  }
  return t17;
}
export default FundraisingEventPageContent;